import React, { useState, useMemo } from "react";
import { api } from "../../../app/Api/ApiSlice";
import { toast } from "react-toastify";
import { useParams, useLocation, Link } from "react-router-dom";
import "../storesStyles.css";

const StoreOrder = () => {
    const location = useLocation();
    const { order } = location.state || {};
    const { name } = useParams();

    const [isDelivered, setIsDelivered] = useState(order?.isDelivered || false);
    const [isInvoiced, setIsInvoiced] = useState(order?.isInvoiced || false);
    const [isPaid, setIsPaid] = useState(order?.isPaid || false);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);

    /* ========================
       Calculations (same logic as Invoice)
    ======================== */
    const subtotal = useMemo(() => {
        if (!order?.items) return 0;
        return order.items.reduce(
            (acc, item) => acc + item.price * item.quantity,
            0
        );
    }, [order]);

    const tax = order?.tax || 0;
    const discount = order?.discount || 0;
    const grandTotal = subtotal + tax - discount;

    /* ========================
       Actions
    ======================== */
    const invoice = async () => {
        try {
            setLoading(true);
            await api.post(`/stores/${name}/manage/invoices`, {
                order: order._id,
                items: order.items,
                customer: order.createdBy._id,
                store: order.store
            });
            setIsInvoiced(true);
            toast.success("Invoice sent");
        } catch (error) {
            console.log(error)
            toast.error(error?.response?.data?.message || "Failed to send invoice");
        } finally {
            setLoading(false);
        }
    };

    const markDelivered = async () => {
        try {
            setLoading(true);
            await api.post(
                `/stores/${name}/manage/orders/${order._id}/delivered`
            );
            setIsDelivered(true);
            toast.success("Marked as delivered");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to mark delivered");
        } finally {
            setLoading(false);
        }
    };

    const markPaid = async () => {
        try {
            setLoading(true);
            await api.post(`/stores/${name}/orders/${order._id}/paid`);
            setIsPaid(true);
            toast.success("Marked as paid");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to mark paid");
        } finally {
            setLoading(false);
        }
    };

    if (!order) return <p className="not-found-msg">Order not found</p>;

    return (
        <div className="store-orders-container">
            <h2 className="page-title">Order</h2>

            {/* ========================
         Order / Invoice Summary
      ======================== */}
            <div className="order-details">
                <div className="order-details-info">
                    <strong>Order ID:</strong>
                    <span>{order._id}</span>
                </div>

                <div className="order-details-info">
                    <strong>Status:</strong>
                    <span
                        className={`order-status ${isPaid ? "Paid" : "Pending"
                            }`}
                    >
                        {isPaid ? "Paid" : "Due"}
                    </span>
                </div>

                <div className="order-details-info">
                    <strong>Delivered:</strong>
                    <span>{isDelivered ? "Yes" : "No"}</span>
                </div>

                <div className="order-details-info">
                    <strong>Invoiced:</strong>
                    <span>{isInvoiced ? "Yes" : "No"}</span>
                </div>

                <div className="order-details-info">
                    <strong>Created On:</strong>
                    <span>
                        {new Date(order.createdAt).toLocaleDateString()}
                    </span>
                </div>
            </div>

            {/* ========================
         Customer Info
      ======================== */}
            <div className="order-details">
                <div className="order-details-info">
                    <strong>Customer:</strong>
                    <span>{order.user?.name}</span>
                </div>

                <div className="order-details-info">
                    <strong>Email:</strong>
                    <span>{order.user?.email}</span>
                </div>

                <div className="order-details-info">
                    <strong>Phone:</strong>
                    <span>{order.user?.phone}</span>
                </div>
            </div>

            {/* ========================
         Items Table (same classNames)
      ======================== */}
            <div className="table-wrapper">
                <table className="orders-table">
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th>Price</th>
                            <th>Qty</th>
                            <th>Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {order.items?.map(item => (
                            <tr key={item._id}>
                                <td>{item.name}</td>
                                <td>${item.price}</td>
                                <td>{item.quantity}</td>
                                <td className="order-total">
                                    ${item.price * item.quantity}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ========================
         Price Breakdown
      ======================== */}
            <div className="price-breakdown">
                <div className="price-row">
                    <span>Subtotal</span>
                    <span>${subtotal}</span>
                </div>

                <div className="price-row">
                    <span>Tax</span>
                    <span>${tax}</span>
                </div>

                {discount > 0 && (
                    <div className="price-row discount">
                        <span>Discount</span>
                        <span>- ${discount}</span>
                    </div>
                )}

                <div className="price-row grand-total">
                    <strong>Total</strong>
                    <strong>${grandTotal}</strong>
                </div>
            </div>

            {/* ========================
         Store Actions (Admin)
      ======================== */}
            <div className="order-actions">
                <button
                    className="btn-primary"
                    onClick={invoice}
                    disabled={loading || isInvoiced}
                >
                    Send Invoice
                </button>

                <button
                    className="btn-secondary"
                    onClick={markDelivered}
                    disabled={loading || isDelivered}
                >
                    Mark Delivered
                </button>

                <button
                    className="btn-secondary"
                    onClick={markPaid}
                    disabled={loading || isPaid}
                >
                    Mark Paid
                </button>
            </div>

            <textarea
                className="order-comment"
                placeholder="Internal comment (optional)"
                value={comment}
                onChange={e => setComment(e.target.value)}
            />

            <Link to={`/stores/${name}/orders`} className="back-link">
                ← Back to orders
            </Link>
        </div>
    );
};

export default StoreOrder;
